/**
 * PURPOSE: Agregar un partido manual al borrador (FTR-0028 WG3, §18 del encargo; D-25).
 * RESPONSIBILITIES: Construir las dos referencias con la gramática real del backend y crear la fila.
 *
 *   NO SOLO "A1 vs A2". Un partido manual admite cualquier par válido: dos slots de grupos DISTINTOS
 *   (`A1 vs B1`), posiciones (`1.º A vs 2.º B`), acumulados, ganadores y perdedores de otra llave, o
 *   un texto libre. Limitar el formulario al caso fácil dejaría fuera justo los partidos que hay que
 *   crear a mano.
 *
 *   LA FASE SE DEDUCE, Y NO ES COSMÉTICO. Dos slots del MISMO grupo son fase de grupos; cualquier
 *   otra combinación es fase final y exige etiqueta. La razón es que el fixture público agrupa por
 *   `idgrupo`: un cruce entre grupos dejado en fase 1 desaparecería de la vista pública.
 *
 *   LA FILA NACE FIJADA (`locked`) y no la reescribe ninguna regeneración.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, utils/programmingCenterModel.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/ManualMatchPanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiPlusCircle } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { groupLetter, referenceLabel } from "../../../../utils/programmingCenterModel.js";
import SidePanel from "../components/SidePanel.jsx";

const REFERENCE_KINDS = [
  { value: "SLOT", label: "Slot de grupo (A1)" },
  { value: "POS", label: "Posición de grupo (1.º A)" },
  { value: "ACUM", label: "Posición acumulada" },
  { value: "WIN", label: "Ganador de un partido" },
  { value: "LOSS", label: "Perdedor de un partido" },
  { value: "TXT", label: "Texto libre" },
];

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const emptySide = () => ({ kind: "SLOT", group: "1", value: "1", matchKey: "", text: "" });

/** Arma la referencia textual exacta que espera el backend. */
export const buildReference = (side) => {
  switch (side.kind) {
    case "SLOT":
      return side.group && side.value ? `SLOT:G${Number(side.group)}:${Number(side.value)}` : "";
    case "POS":
      return side.group && side.value ? `POS:G${Number(side.group)}:${Number(side.value)}` : "";
    case "ACUM":
      return side.value ? `ACUM:${Number(side.value)}` : "";
    case "WIN":
      return side.matchKey ? `WIN:${side.matchKey.trim()}` : "";
    case "LOSS":
      return side.matchKey ? `LOSS:${side.matchKey.trim()}` : "";
    case "TXT":
      return side.text.trim() ? `TXT:${side.text.trim()}` : "";
    default:
      return "";
  }
};

/** Fase 1 solo cuando ambos lados son slots del MISMO grupo; cualquier otra cosa es fase final. */
export const derivePhase = (home, away) => {
  const bothSlots = home.kind === "SLOT" && away.kind === "SLOT";
  const sameGroup = Number(home.group) === Number(away.group);
  return bothSlots && sameGroup ? 1 : 2;
};

const SideEditor = ({ title, side, onChange, groups, rows }) => {
  const set = (patch) => onChange({ ...side, ...patch });

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">{title}</p>
      <select value={side.kind} onChange={(event) => set({ kind: event.target.value })} className={inputClass}>
        {REFERENCE_KINDS.map((kind) => (
          <option key={kind.value} value={kind.value}>
            {kind.label}
          </option>
        ))}
      </select>

      {side.kind === "SLOT" || side.kind === "POS" ? (
        <div className="grid grid-cols-2 gap-2">
          <select
            value={side.group}
            onChange={(event) => set({ group: event.target.value })}
            className={inputClass}
            aria-label={`Grupo de ${title}`}
          >
            {groups.map((group) => (
              <option key={group} value={group}>
                Grupo {groupLetter(group)}
              </option>
            ))}
          </select>
          <input
            type="number"
            min="1"
            max="6"
            value={side.value}
            onChange={(event) => set({ value: event.target.value })}
            className={inputClass}
            aria-label={side.kind === "SLOT" ? `Slot de ${title}` : `Posición de ${title}`}
          />
        </div>
      ) : null}

      {side.kind === "ACUM" ? (
        <input
          type="number"
          min="1"
          value={side.value}
          onChange={(event) => set({ value: event.target.value })}
          className={inputClass}
          aria-label={`Posición acumulada de ${title}`}
        />
      ) : null}

      {side.kind === "WIN" || side.kind === "LOSS" ? (
        <select
          value={side.matchKey}
          onChange={(event) => set({ matchKey: event.target.value })}
          className={inputClass}
          aria-label={`Partido de referencia de ${title}`}
        >
          <option value="">Elige un partido</option>
          {rows.map((row) => (
            <option key={row.draftKey} value={row.draftKey}>
              {row.draftKey} · {row.label}
            </option>
          ))}
        </select>
      ) : null}

      {side.kind === "TXT" ? (
        <input
          type="text"
          maxLength={28}
          value={side.text}
          onChange={(event) => set({ text: event.target.value })}
          placeholder="Ej. Invitado"
          className={inputClass}
          aria-label={`Texto de ${title}`}
        />
      ) : null}

      <p className="text-xs font-black text-slate-600">
        {buildReference(side) ? referenceLabel(buildReference(side)) : "Sin definir"}
      </p>
    </div>
  );
};

const ManualMatchPanel = ({ open, onClose }) => {
  const { rows, actions, busy, draft } = useProgrammingCenter();
  const [home, setHome] = useState(emptySide);
  const [away, setAway] = useState(() => ({ ...emptySide(), value: "2" }));
  const [dateNo, setDateNo] = useState("1");
  const [tagFinal, setTagFinal] = useState("");

  useEffect(() => {
    if (!open) return;
    setHome(emptySide());
    setAway({ ...emptySide(), value: "2" });
    setDateNo("1");
    setTagFinal("");
  }, [open]);

  const groups = useMemo(() => {
    const sizes = draft?.draft?.group_sizes || [];
    const count = Math.max(sizes.length, 1);
    return Array.from({ length: count }, (unused, position) => position + 1);
  }, [draft]);

  const referenceRows = useMemo(
    () => rows.filter((row) => !row.isBye && !row.isOrphan).slice(0, 200),
    [rows]
  );

  const homeRef = buildReference(home);
  const awayRef = buildReference(away);
  const phase = derivePhase(home, away);
  const needsTag = phase === 2;
  const sameSide = homeRef && homeRef === awayRef;

  const valid =
    Boolean(homeRef) &&
    Boolean(awayRef) &&
    !sameSide &&
    Number(dateNo) > 0 &&
    (!needsTag || tagFinal.trim().length > 0);

  const submit = async () => {
    const result = await actions.createManual({
      dateNo: Number(dateNo),
      homeRef,
      awayRef,
      ...(needsTag ? { tagFinal: tagFinal.trim() } : {}),
    });
    if (result.ok) onClose();
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Agregar partido manual"
      subtitle="Cualquier par de referencias válidas, incluidos cruces entre grupos"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <SideEditor title="Local" side={home} onChange={setHome} groups={groups} rows={referenceRows} />
        <SideEditor title="Visita" side={away} onChange={setAway} groups={groups} rows={referenceRows} />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
            {phase === 2 ? "Etapa" : "Jornada"}
          </span>
          <input
            type="number"
            min="1"
            value={dateNo}
            onChange={(event) => setDateNo(event.target.value)}
            className={inputClass}
          />
        </label>
        {needsTag ? (
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
              Etiqueta de fase final
            </span>
            <input
              type="text"
              value={tagFinal}
              onChange={(event) => setTagFinal(event.target.value)}
              placeholder="Ej. Cuartos de Final 1"
              className={inputClass}
            />
          </label>
        ) : null}
      </div>

      <p className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs font-bold text-blue-800">
        {phase === 1
          ? "Ambos lados son slots del mismo grupo: se creará en la fase de grupos."
          : "Los lados no pertenecen al mismo grupo: se creará en la fase final, que es lo que mantiene el partido visible en el fixture público."}
      </p>

      {sameSide ? (
        <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-black text-rose-700">
          Los dos lados no pueden ser el mismo.
        </p>
      ) : null}

      <button
        type="button"
        onClick={submit}
        disabled={!valid || Boolean(busy)}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiPlusCircle className="text-sm" />
        Agregar {referenceLabel(homeRef) || "?"} vs {referenceLabel(awayRef) || "?"}
      </button>
      <p className="mt-2 text-[11px] font-bold text-slate-500">
        Crea una fila del borrador fijada, que ninguna regeneración reescribe. El partido real se
        crea al materializar.
      </p>
    </SidePanel>
  );
};

export default ManualMatchPanel;
